import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  useDealerPortal,
  DEFAULT_CUSTOMERS_PAGE_SIZE,
  CUSTOMERS_PAGE_SIZES,
} from "@/hooks/useDealerPortal";
import { usePersistedPageSize } from "@/hooks/usePersistedPageSize";
import { useLanguage } from "@/context/LanguageContext";
import { dealerPortalLabels } from "@/i18n";
import { DealerPortalCreateCustomerDialog } from "./DealerPortalCreateCustomerDialog";
import { DealerPortalCustomerCardDialog } from "./DealerPortalCustomerCardDialog";
import {
  Mail,
  Phone,
  ChevronLeft,
  ChevronRight,
  Search,
  Clock,
  FileText,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type {
  DealerPortalCustomer,
  DealerPortalCustomerType,
} from "@/types/dealerPortal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CUSTOMER_TYPE_TABS: {
  value: DealerPortalCustomerType | null;
  labelKey: string;
}[] = [
  { value: "PRIVATE", labelKey: "typeTabPrivate" },
  { value: "DEALER", labelKey: "typeTabDealer" },
  { value: "BUSINESS", labelKey: "typeTabBusiness" },
];

const CUSTOMER_TYPE_BADGE_CLASS: Record<DealerPortalCustomerType, string> = {
  PRIVATE:
    "text-[10px] px-1.5 py-0 font-normal bg-slate-100 text-slate-800 border-slate-200 dark:bg-slate-700/60 dark:text-slate-200 dark:border-slate-600",
  DEALER:
    "text-[10px] px-1.5 py-0 font-normal bg-sky-100 text-sky-800 border-sky-200 dark:bg-sky-900/50 dark:text-sky-200 dark:border-sky-700",
  BUSINESS:
    "text-[10px] px-1.5 py-0 font-normal bg-violet-100 text-violet-800 border-violet-200 dark:bg-violet-900/50 dark:text-violet-200 dark:border-violet-700",
};

export function DealerPortalCustomersTab() {
  const { language } = useLanguage();
  const d = dealerPortalLabels(language);
  const c = d.customers as Record<string, string>;
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(
    null,
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePersistedPageSize(
    "pageSize:dealerPortalCustomers",
    DEFAULT_CUSTOMERS_PAGE_SIZE,
    [...CUSTOMERS_PAGE_SIZES],
  );
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState<string | null>(null);
  const [roleFilter, setRoleFilter] = useState<"buyer" | "seller" | null>(null);
  const [typeFilter, setTypeFilter] = useState<DealerPortalCustomerType | null>(
    null,
  );

  const { customersPaginated } = useDealerPortal({
    customersPage: page,
    customersPageSize: pageSize,
    customersSearch: appliedSearch,
    customersRole: roleFilter,
    customersType: typeFilter,
  });

  const paginated = customersPaginated.data;
  const items = paginated?.items ?? [];
  const total = paginated?.total ?? 0;
  const totalPages =
    pageSize > 0 ? Math.max(1, Math.ceil(total / pageSize)) : 1;
  const isLoading = customersPaginated.isLoading;

  useEffect(() => {
    setPage(1);
  }, [pageSize, appliedSearch, roleFilter, typeFilter]);

  const handleSearch = () => {
    const trimmed = searchInput.trim();
    setAppliedSearch(trimmed === "" ? null : trimmed);
    setPage(1);
  };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">{c.title ?? "Kunder"}</h2>

      {/* Kundtyp-flikar (Privat, Handlare, Företag) */}
      <div className="flex gap-1 p-1 rounded-lg border bg-muted/30 w-fit">
        <button
          type="button"
          onClick={() => (setTypeFilter(null), setPage(1))}
          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
            typeFilter === null
              ? "bg-background border shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {c.roleFilterAll ?? "Alla"}
        </button>
        {CUSTOMER_TYPE_TABS.map(({ value, labelKey }) => (
          <button
            key={value}
            type="button"
            onClick={() => (setTypeFilter(value), setPage(1))}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              typeFilter === value
                ? "bg-background border shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {c[labelKey] ?? value}
          </button>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {c.role ?? "Roll"}:
          </span>
          <Select
            value={roleFilter ?? "all"}
            onValueChange={(v) => {
              setRoleFilter(v === "all" ? null : (v as "buyer" | "seller"));
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[120px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{c.roleFilterAll ?? "Alla"}</SelectItem>
              <SelectItem value="buyer">{c.buyer ?? "Köpare"}</SelectItem>
              <SelectItem value="seller">{c.seller ?? "Säljare"}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-1 min-w-0 items-center gap-2">
          <Input
            type="text"
            placeholder={c.searchPlaceholder ?? "Personnummer, namn, mejl..."}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className="h-9 flex-1 min-w-0 max-w-sm"
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="gap-1.5 h-9 shrink-0"
            onClick={handleSearch}
          >
            <Search className="h-4 w-4" />
            {c.search ?? "Sök"}
          </Button>
        </div>
        <Button
          size="sm"
          className="shrink-0 w-full sm:w-auto"
          onClick={() => setCreateOpen(true)}
        >
          {c.addCustomer ?? "Lägg till kund"}
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground py-6">
          {c.loading ?? "Laddar..."}
        </p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6">
          {appliedSearch
            ? (c.noMatches ?? "Inga kunder matchar sökningen.")
            : (c.empty ?? "Inga kunder än.")}
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-2 py-0.5">
            {items.map((cust) => (
              <button
                key={cust.id}
                type="button"
                onClick={() => setSelectedCustomerId(cust.id)}
                className="flex items-center gap-3 rounded-lg border bg-card px-3 py-2.5 text-left w-full hover:shadow-md transition-shadow"
              >
                <div className="min-w-0 flex-1 flex flex-row flex-wrap items-center gap-x-3 gap-y-0.5">
                  <p className="font-medium text-sm leading-tight truncate basis-full sm:basis-auto">
                    {cust.name || "—"}
                  </p>
                  {cust.customerType && (
                    <Badge
                      variant="secondary"
                      className={CUSTOMER_TYPE_BADGE_CLASS[cust.customerType]}
                    >
                      {cust.customerType === "PRIVATE"
                        ? (c.typePrivate ?? "Privatkund")
                        : cust.customerType === "DEALER"
                          ? (c.typeDealer ?? "Handlare")
                          : (c.typeBusiness ?? "Företag")}
                    </Badge>
                  )}
                  {(cust.isBuyer || cust.isSeller) && (
                    <span className="flex flex-wrap gap-1">
                      {cust.isBuyer && (
                        <Badge
                          variant="secondary"
                          className="text-[10px] px-1.5 py-0 font-normal bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-700"
                        >
                          {c.buyer ?? "Köpare"}
                        </Badge>
                      )}
                      {cust.isSeller && (
                        <Badge
                          variant="secondary"
                          className="text-[10px] px-1.5 py-0 font-normal bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-700"
                        >
                          {c.seller ?? "Säljare"}
                        </Badge>
                      )}
                    </span>
                  )}
                  {cust.email ? (
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-0 truncate">
                      <Mail className="h-3 w-3 shrink-0" />
                      <span className="truncate">{cust.email}</span>
                    </span>
                  ) : null}
                  {cust.phone ? (
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-0 truncate">
                      <Phone className="h-3 w-3 shrink-0" />
                      <span className="truncate">{cust.phone}</span>
                    </span>
                  ) : null}
                  {(cust._count?.historyEvents != null ||
                    cust._count?.notes != null) && (
                    <span className="flex items-center gap-2 text-xs text-muted-foreground ml-auto shrink-0">
                      {cust._count?.historyEvents != null && (
                        <span
                          className="flex items-center gap-1"
                          title={c.tabHistory ?? "Historik"}
                        >
                          <Clock className="h-3 w-3 shrink-0" />
                          {cust._count.historyEvents}
                        </span>
                      )}
                      {cust._count?.notes != null && (
                        <span
                          className="flex items-center gap-1"
                          title={c.tabNotes ?? "Anteckningar"}
                        >
                          <FileText className="h-3 w-3 shrink-0" />
                          {cust._count.notes}
                        </span>
                      )}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>

          {totalPages > 1 || total > 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {c.pageSize ?? "Per sida"}:
                </span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(val) => {
                    setPageSize(Number(val));
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[70px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CUSTOMERS_PAGE_SIZES.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground">
                  {total} {c.total ?? "totalt"}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm px-2">
                  {c.page ?? "Sida"} {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : null}
        </>
      )}

      <DealerPortalCustomerCardDialog
        customerId={selectedCustomerId}
        open={!!selectedCustomerId}
        onClose={() => setSelectedCustomerId(null)}
        onUpdated={() => void customersPaginated.refetch()}
      />

      <DealerPortalCreateCustomerDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </div>
  );
}
